/**
 * Schwebende Meldung (F-45) - rein darstellend: Text, optional ein Knopf
 * (z. B. „Rückgängig“) und Schliessen. Kartenstil wie die Termin-Erinnerung,
 * Farben nur aus dem zentralen Farbsystem. Wer sie einblendet, entscheidet
 * auch, wann sie verschwindet; Hover und Fokus melden sich ueber `onHalten`.
 *
 * Keine eigene Live-Region: Die Ansage macht der Aufrufer ueber eine
 * dauerhaft vorhandene Region (eine neu eingehaengte wird nicht zuverlaessig
 * vorgelesen). Verschwindet der Knopf, waehrend er den Fokus hat (nach
 * „Rückgängig“ kommt die Rueckmeldung ohne Knopf), geht der Fokus auf
 * Schliessen statt auf den Seitenanfang.
 */
import { useLayoutEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils/cn';

export interface ToastAktion {
  label: string;
  /** Tooltip, z. B. das Tastenkuerzel. */
  titel?: string;
  onClick: () => void;
  disabled?: boolean;
}

export function Toast({ text, aktion, onSchliessen, schliessenLabel, fehler, onHalten }: {
  text: string;
  aktion?: ToastAktion;
  onSchliessen: () => void;
  schliessenLabel: string;
  /** Rueckmeldung, dass etwas nicht ging - gleicher Stil, Rand in der Warnfarbe. */
  fehler?: boolean;
  /** `true` solange die Maus darauf liegt oder der Fokus darin ist. */
  onHalten?: (halten: boolean) => void;
}) {
  const schliessenRef = useRef<HTMLButtonElement>(null);
  const fokusWeitergeben = useRef(false);
  useLayoutEffect(() => {
    if (aktion || !fokusWeitergeben.current) return;
    fokusWeitergeben.current = false;
    schliessenRef.current?.focus();
  }, [aktion]);

  return (
    <div
      onMouseEnter={() => onHalten?.(true)}
      onMouseLeave={() => onHalten?.(false)}
      onFocus={() => onHalten?.(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onHalten?.(false); }}
      className={cn(
        'pointer-events-auto flex w-full items-center gap-2 rounded-lg border bg-surface-raised py-2 pl-3.5 pr-2 shadow-lg',
        fehler ? 'border-danger/60' : 'border-line-strong',
      )}
    >
      {/* Hoechstens zwei Zeilen: ein langer Titel wird abgeschnitten, „gelöscht“ bleibt meist sichtbar. */}
      <p className="line-clamp-2 min-w-0 flex-1 break-words text-sm text-ink" title={text}>{text}</p>
      {aktion && (
        <button
          type="button"
          onClick={(e) => {
            fokusWeitergeben.current = document.activeElement === e.currentTarget;
            aktion.onClick();
          }}
          disabled={aktion.disabled}
          title={aktion.titel}
          className="shrink-0 rounded px-2 py-1 text-sm font-medium text-accent-text hover:bg-surface hover:underline disabled:opacity-50"
        >
          {aktion.label}
        </button>
      )}
      <button
        ref={schliessenRef}
        type="button"
        onClick={onSchliessen}
        aria-label={schliessenLabel}
        className="grid size-7 shrink-0 place-items-center rounded text-ink-faint hover:text-ink"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
